import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, CheckCircle2, CreditCard, Landmark } from "lucide-react";
import { Logo } from "@/components/Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function InvoicePage() {
  const [params] = useSearchParams();
  const jobId = (params.get("job") || "").toUpperCase();
  const sessionId = params.get("session_id");
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [paying, setPaying] = useState(false);

  const load = () => axios.get(`${API}/jobs/${jobId}/invoice`).then((r) => setData(r.data)).catch(() => setErr("Invoice not found. Please check the link or call us on 03 9113 6005."));

  useEffect(() => {
    if (!jobId) { setErr("No job specified."); return; }
    if (sessionId) {
      let tries = 0;
      const poll = async () => {
        try {
          const { data: st } = await axios.get(`${API}/payments/status/${sessionId}`);
          if (st.status === "collected") { load(); return; }
        } catch {}
        if (++tries < 6) setTimeout(poll, 2000);
        else load();
      };
      poll();
    } else load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId]);

  const payByCard = async () => {
    setPaying(true);
    try {
      const inv = data.invoice;
      const { data: res } = await axios.post(`${API}/jobs/${jobId}/payment/card`, {
        method: "card",
        hours: inv.hours,
        callout_minutes: inv.callout_minutes,
        rate: inv.rate,
        extras: inv.extras || [],
        collected_by: "Customer (online invoice)",
        origin_url: window.location.origin,
        return_path: "invoice",
      });
      window.location.href = res.checkout_url;
    } catch (e2) {
      toast.error(e2.response?.data?.detail || "Could not start card payment.");
      setPaying(false);
    }
  };

  if (err) return <div className="flex min-h-screen items-center justify-center bg-slate-100 px-5 text-center text-sm text-slate-600" data-testid="invoice-error">{err}</div>;
  if (!data) return <div className="flex min-h-screen items-center justify-center bg-slate-100"><Loader2 className="h-8 w-8 animate-spin text-blaze" /></div>;

  const inv = data.invoice;
  const rows = [
    [`$${inv.rate}/hr × ${inv.hours} hrs${inv.callout_minutes ? ` (incl. ${inv.callout_minutes} min call-out)` : ""}`, inv.job_total],
    ...(inv.extras || []).map((e) => [`Extra — ${e.reason}`, e.amount]),
    ...(inv.booking_fee_paid ? [["Booking fee already paid", -inv.booking_fee_paid]] : []),
  ];

  return (
    <div className="min-h-screen bg-slate-100 px-4 py-10" data-testid="invoice-page">
      <div className="mx-auto max-w-lg">
        <div className="mb-6 flex justify-center"><Logo /></div>
        <div className="overflow-hidden rounded-3xl border border-line bg-white shadow-xl">
          <div className="bg-slate-900 p-6 text-center text-white">
            <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-blue-300">Invoice</p>
            <h1 className="mt-1 font-display text-2xl font-bold">Job {data.job_id}</h1>
            <p className="mt-1 text-xs text-slate-300">{data.customer}{data.moving_date ? ` · ${data.moving_date}` : ""}</p>
          </div>
          <div className="p-6">
            <div className="divide-y divide-line/60 text-sm">
              {rows.map(([label, amt], i) => (
                <div key={i} className="flex items-center justify-between py-2.5">
                  <span className="pr-3 text-slate-600">{label}</span>
                  <span className={`font-semibold ${amt < 0 ? "text-green-700" : "text-slate-900"}`}>{amt < 0 ? `-$${Math.abs(amt).toFixed(2)}` : `$${Number(amt).toFixed(2)}`}</span>
                </div>
              ))}
              <div className="flex items-center justify-between py-3">
                <span className="font-display text-base font-bold uppercase text-slate-900">Amount Due</span>
                <span className="font-display text-2xl font-bold text-blaze" data-testid="invoice-total">${Number(inv.total).toFixed(2)}</span>
              </div>
            </div>

            {data.paid ? (
              <div className="mt-4 rounded-2xl border border-green-300 bg-green-50 p-5 text-center" data-testid="invoice-paid">
                <CheckCircle2 className="mx-auto h-10 w-10 text-green-600" />
                <p className="mt-2 font-display text-lg font-bold uppercase text-green-700">Paid — Thank You!</p>
                <p className="mt-1 text-xs text-slate-600">{data.paid_at ? `Received ${data.paid_at}` : ""} A paid invoice has been emailed to you for your records.</p>
              </div>
            ) : (
              <>
                <button onClick={payByCard} disabled={paying} className="mt-4 flex w-full items-center justify-center gap-2 rounded-xl bg-blaze py-4 text-sm font-bold uppercase tracking-[0.15em] text-white active:scale-[0.99] disabled:opacity-60" data-testid="invoice-pay-card">
                  {paying ? <Loader2 className="h-4 w-4 animate-spin" /> : <CreditCard className="h-4 w-4" />} Pay ${Number(inv.card_total).toFixed(2)} by Card
                </button>
                <p className="mt-1.5 text-center text-[11px] text-slate-400">Card payments include a 2.5% surcharge (${Number(inv.card_surcharge || 0).toFixed(2)}).</p>
                {data.bank?.bsb && (
                  <div className="mt-4 rounded-2xl border border-line bg-slate-50 p-4" data-testid="invoice-bank">
                    <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-slate-700"><Landmark className="h-4 w-4 text-blaze" /> Or pay by bank transfer</p>
                    <div className="mt-2 space-y-1 text-sm text-slate-700">
                      {data.bank.account_name && <p><span className="text-slate-400">Account:</span> {data.bank.account_name}</p>}
                      <p><span className="text-slate-400">BSB:</span> {data.bank.bsb} · <span className="text-slate-400">Acc No:</span> {data.bank.account_number}</p>
                      <p><span className="text-slate-400">Reference:</span> <strong>{data.job_id}</strong></p>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
        <p className="mt-4 text-center text-xs text-slate-400">Questions? Call us on <a href="tel:+61391136005" className="font-semibold text-blaze">03 9113 6005</a></p>
      </div>
    </div>
  );
}
